import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, BarChart3 } from 'lucide-react';
import { EmptyState, ErrorState, PageHeader } from '../components/common/States';
import { LaneDot } from '../components/items/Tags';
import { Skeleton } from '../components/ui/skeleton';
import { Hint } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useMeta } from '../hooks/queries';
import { api } from '../lib/api';
import { diagnosisMeta, fieldLabel, priorityMeta, universeMeta } from '../lib/taxonomy';
import { fmtDate, fmtDateTime, fmtDec, fmtEur, fmtInt, fmtK, fmtPct } from '../lib/format';
import { cn } from '../lib/utils';

const toExplore = (navigate, params) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v != null && sp.append(k, v));
  navigate(`/explore?${sp.toString()}`);
};

function Kpi({ label, value, sub, onClick }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn('group rounded-lg border bg-card px-4 py-3 text-left', onClick && 'transition hover:border-foreground/20')}
    >
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        {label}
        {onClick && <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" />}
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">{sub}</div>}
    </Comp>
  );
}

function Card({ title, description, children, className }) {
  return (
    <section className={cn('rounded-lg border bg-card', className)}>
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <div className="p-2">{children}</div>
    </section>
  );
}

/** Diagnosis × exit lane heat table, values in k€. Single-hue sequential scale. */
function Matrix({ matrix, onCell }) {
  const rows = matrix?.rows || [];
  const cols = matrix?.cols || [];
  const values = matrix?.values || [];
  const max = Math.max(1, ...values.flat().map((v) => Number(v) || 0));
  const rowTotals = values.map((r) => r.reduce((a, b) => a + (Number(b) || 0), 0));
  const colTotals = cols.map((_, j) => values.reduce((a, r) => a + (Number(r[j]) || 0), 0));
  if (!rows.length) return <p className="p-4 text-sm text-muted-foreground">No data.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-[2px] text-sm">
        <thead>
          <tr>
            <th className="w-40" />
            {cols.map((c) => (
              <th key={c} className="px-2 pb-1 text-left align-bottom text-[11px] font-medium text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <LaneDot lane={c} /> {c}
                </span>
              </th>
            ))}
            <th className="px-2 pb-1 text-right text-[11px] font-medium text-muted-foreground">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r}>
              <th className="whitespace-nowrap px-2 text-left text-[13px] font-medium">
                <Hint text={diagnosisMeta(r).description}>
                  <button type="button" className="hover:underline" onClick={() => onCell({ diagnosis: r })}>
                    {r}
                  </button>
                </Hint>
              </th>
              {cols.map((c, j) => {
                const v = Number(values[i]?.[j]) || 0;
                const t = v / max;
                return (
                  <td key={c} className="p-0">
                    <button
                      type="button"
                      disabled={!v}
                      onClick={() => onCell({ diagnosis: r, exit_lane: c })}
                      title={`${r} × ${c}: ${fmtEur(v)}`}
                      className={cn(
                        'relative h-10 w-full min-w-[72px] rounded-[4px] px-2 text-right tabular-nums transition hover:ring-2 hover:ring-foreground/40',
                        !v && 'cursor-default text-muted-foreground/50 hover:ring-0'
                      )}
                      style={{ backgroundColor: v ? `rgba(79, 70, 229, ${0.06 + t * 0.74})` : 'hsl(var(--muted) / 0.5)' }}
                    >
                      <span className={cn('text-[13px]', t > 0.5 ? 'font-medium text-white' : 'text-foreground')}>{v ? fmtK(v) : '·'}</span>
                    </button>
                  </td>
                );
              })}
              <td className="px-2 text-right text-[13px] font-medium tabular-nums">{fmtK(rowTotals[i])}</td>
            </tr>
          ))}
          <tr>
            <th className="px-2 text-left text-[11px] font-medium text-muted-foreground">Total</th>
            {colTotals.map((v, j) => (
              <td key={cols[j]} className="px-2 pt-1 text-right text-[13px] font-medium tabular-nums">
                <button type="button" className="hover:underline" onClick={() => onCell({ exit_lane: cols[j] })}>
                  {fmtK(v)}
                </button>
              </td>
            ))}
            <td className="px-2 pt-1 text-right text-[13px] font-semibold tabular-nums">{fmtK(rowTotals.reduce((a, b) => a + b, 0))}</td>
          </tr>
        </tbody>
      </table>
      <p className="px-2 pt-2 text-[11px] text-muted-foreground">Values in k€ at cost. Click a cell to explore those items.</p>
    </div>
  );
}

function GroupTable({ rows = [], label, onRow, describe }) {
  const maxV = Math.max(1, ...rows.map((r) => Number(r.value) || 0));
  if (!rows.length) return <p className="p-4 text-sm text-muted-foreground">No data.</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>{label}</TableHead>
          <TableHead className="text-right">Items</TableHead>
          <TableHead className="text-right">Units</TableHead>
          <TableHead className="w-[30%] text-right">Value</TableHead>
          <TableHead className="text-right">m³</TableHead>
          <TableHead className="text-right">Med. age</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key} className="cursor-pointer" onClick={() => onRow(r.key)} title={describe?.(r.key)}>
            <TableCell className="font-medium">{r.key || '(none)'}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtInt(r.n)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtInt(r.units)}</TableCell>
            <TableCell>
              <div className="flex items-center justify-end gap-2">
                <div className="hidden h-1.5 flex-1 overflow-hidden rounded-full bg-muted sm:block">
                  <div className="h-full rounded-full bg-indigo-500/70" style={{ width: `${((Number(r.value) || 0) / maxV) * 100}%` }} />
                </div>
                <span className="w-20 text-right font-medium tabular-nums">{fmtEur(r.value)}</span>
              </div>
            </TableCell>
            <TableCell className="text-right tabular-nums">{fmtDec(r.m3)}</TableCell>
            <TableCell className="text-right tabular-nums">{r.median_age != null ? `${fmtInt(r.median_age)}d` : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function BrandsTable({ brands = [], onRow }) {
  if (!brands.length) return <p className="p-4 text-sm text-muted-foreground">No data.</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Brand</TableHead>
          <TableHead className="text-right">Variants</TableHead>
          <TableHead className="text-right">Problem value</TableHead>
          <TableHead className="text-right">Stock value</TableHead>
          <TableHead className="w-[22%]">Frozen share</TableHead>
          <TableHead className="text-right">Recently bought (ask supplier)</TableHead>
          <TableHead>Vendor</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {brands.map((b) => {
          const share = Number(b.frozen_share) || 0;
          const pct = share > 1 ? share / 100 : share;
          const hot = pct >= 0.6;
          return (
            <TableRow key={b.brand} className="cursor-pointer" onClick={() => onRow(b.brand)}>
              <TableCell className="font-medium">{b.brand}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtInt(b.variants)}</TableCell>
              <TableCell className="text-right font-medium tabular-nums">{fmtEur(b.problem_value)}</TableCell>
              <TableCell className="text-right tabular-nums text-muted-foreground">{fmtEur(b.stock_value)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className={cn('h-full rounded-full', hot ? 'bg-red-500' : 'bg-slate-400 dark:bg-slate-500')} style={{ width: `${Math.min(100, pct * 100)}%` }} />
                  </div>
                  <span className={cn('w-10 text-right text-xs tabular-nums', hot && 'font-semibold text-red-600 dark:text-red-400')}>{fmtPct(pct)}</span>
                </div>
              </TableCell>
              <TableCell className="text-right tabular-nums">{b.return_eligible_value ? fmtEur(b.return_eligible_value) : <span className="text-muted-foreground">—</span>}</TableCell>
              <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{b.vendor || '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function MissingFields({ rows = [], total }) {
  const max = Math.max(1, ...rows.map((r) => Number(r.n) || 0));
  if (!rows.length) return <p className="p-4 text-sm text-muted-foreground">Nothing missing.</p>;
  return (
    <ul className="space-y-1.5 p-2">
      {rows.map((r) => (
        <li key={r.field} className="grid grid-cols-[120px_1fr_auto] items-center gap-2 text-sm" title={`${fmtInt(r.n)} items miss ${fieldLabel(r.field)}`}>
          <span className="truncate">{fieldLabel(r.field)}</span>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-amber-500/80" style={{ width: `${(Number(r.n) / max) * 100}%` }} />
          </div>
          <span className="w-20 text-right text-xs tabular-nums text-muted-foreground">
            {fmtInt(r.n)}
            {total ? ` · ${fmtPct(r.n / total)}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

const CLASSIFICATION_ORDER = ['universe', 'diagnosis', 'exit_lane', 'priority'];

/** How items are classified: the same live definitions as the (i) dialogs on Explore. */
function Classification({ definitions }) {
  if (!definitions) return <p className="p-3 text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="grid gap-6 p-3 text-sm md:grid-cols-2 xl:grid-cols-4">
      {CLASSIFICATION_ORDER.filter((k) => definitions[k]).map((k) => {
        const d = definitions[k];
        return (
          <div key={k} className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{d.title}</h3>
            <p className="text-xs text-muted-foreground">{d.plain}</p>
            <dl className="space-y-2">
              {d.values.map((v, i) => (
                <div key={v.value}>
                  <dt className="flex items-center gap-1.5 font-medium">
                    <span className="w-4 text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                    {k === 'exit_lane' && <LaneDot lane={v.value} />}
                    {v.value}
                  </dt>
                  <dd className="pl-[22px] text-muted-foreground">{v.plain}</dd>
                  <dd className="pl-[22px] font-mono text-[11px] text-foreground/70">{v.rule}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const { data: meta } = useMeta();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['overview'], queryFn: api.overview });
  const go = (params) => toExplore(navigate, params);
  const t = data?.totals || {};

  return (
    <div className="w-full space-y-5 px-3 pb-16 pt-4 sm:px-6 lg:pt-6">
      <PageHeader
        title="Overview"
        description={
          meta?.snapshot_date
            ? `Snapshot of ${fmtDate(meta.snapshot_date)}${meta.last_run_at ? ` · refreshed ${fmtDateTime(meta.last_run_at)}` : ''}`
            : 'Where the problem stock sits, and why.'
        }
      />
      {error && <ErrorState error={error} onRetry={refetch} title="Could not load the overview" />}
      {isLoading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[88px] rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-80 rounded-lg" />
        </div>
      )}
      {data && !t.n && <EmptyState icon={BarChart3} title="No problem stock in this snapshot" />}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi label="Problem value" value={fmtEur(t.value)} sub={t.stock_value ? `${fmtPct(t.value / t.stock_value)} of ${fmtEur(t.stock_value)} stock` : null} onClick={() => navigate('/explore')} />
            <Kpi label="Items / units" value={fmtInt(t.n)} sub={`${fmtInt(t.units)} units · ${fmtDec(t.m3)} m³`} />
            <Kpi label="Worth asking the supplier" value={fmtEur(t.supplier_return_value)} sub={`${fmtInt(t.supplier_return_n)} items bought in the last 12 months`} onClick={() => go({ supplier_return: '1' })} />
            <Kpi label="Below break-even" value={fmtEur(t.below_breakeven_value)} sub={`${fmtInt(t.below_breakeven_n)} items priced under break-even`} />
            <Kpi label="Second chance (excluded)" value={fmtEur(t.sc_value)} sub={`${fmtInt(t.sc_n)} items, handled by the markdown engine`} />
            <Kpi label="Median listing" value={t.completeness_median != null ? `${fmtInt(t.completeness_median)}%` : '—'} sub="completeness" onClick={() => go({ max_completeness: '50' })} />
          </div>

          <Card title="Why it's stuck × way out" description="Rows: why the stock is stuck. Columns: the best way to sell it. Click a cell to see those products.">
            <Matrix matrix={data.matrix} onCell={go} />
          </Card>

          <div className="grid gap-5 xl:grid-cols-2">
            <Card title="By priority">
              <GroupTable rows={data.by_priority} label="Priority" onRow={(k) => go({ priority: k })} describe={(k) => priorityMeta(k).description} />
            </Card>
            <Card title="By universe">
              <GroupTable rows={data.by_universe} label="Universe" onRow={(k) => go({ universe: k })} describe={(k) => universeMeta(k).description} />
            </Card>
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Card title="By category">
              <GroupTable rows={data.by_category} label="Category" onRow={(k) => go({ category: k })} />
            </Card>
            <Card title="Missing listing fields" description="How many problem items miss each field.">
              <MissingFields rows={data.missing_fields} total={t.n} />
            </Card>
          </div>

          <Card title="Brands" description="Frozen share = problem value / total stock value of the brand (red ≥ 60%).">
            <BrandsTable brands={data.brands} onRow={(b) => go({ brand: b })} />
          </Card>

          <Card title="How items are classified" description="How Toxy decides the stock group, why it's stuck, the way out and the priority. Updated every night.">
            <Classification definitions={meta?.definitions} />
          </Card>
        </>
      )}
    </div>
  );
}
